import React, { useEffect, useState, useRef, useMemo } from "react"
import dayjs from "dayjs"
import { Link } from "gatsby"
import EntitlementsForm from "../EntitlementsForm/EntitlementsForm"
import { LoadingSpinner } from "terra-one"
import {
  TrialFormStart,
  ProductSummary,
  LayoutGeneral,
  LayoutFAQs,
} from "trimble-terra"
import "./TrialWorkFlow.scss"
import {
  formatPageLinks,
  updateDownloadLink,
} from "../../utils/organism-formatting/formatPageLinks"
import getConfigTID from "../../utils/functions/getConfigTID"
import { refreshAuthToken } from "../../utils/functions/refreshAuthToken"

import { triggerTrialStarted } from "../Datalayer/DatalayerTrial"

import errorImg from "../../images/error.webp"
import {
  getTrialFormId,
  checkForHealth,
  startTrial,
  getTrialDetails,
} from "../../utils/functions/getTrialForm"
import {
  createProductDetailPageLink,
  createProductTrialPageLink,
} from "../../components/CTAButtons/CTAButtonsConnector"

const TrialWorkFlow = ({
  data,
  appData,
  locale,
  innerPageNavData,
  setInnerPageNavData,
  region,
  regionList,
  siteIdentifier,
  dataStorageEnv,
  dataStorageSpaceId,
  dataStorageAccessToken,
  product,
}) => {
  const [auth, setAuth] = useState(false)
  const [checkHealthOfTrialsAPI, setCheckHealthOfTrialsAPI] = useState(false)
  const [loading, setLoading] = useState(false)
  const [activeTrial, setActiveTrial] = useState(null)
  const [endedTrial, setEndedTrial] = useState(false)
  const [error, setError] = useState(false)
  const [errorText, setErrorText] = useState(true)
  const [daysLeftOfTrial, setDaysLeftOfTrial] = useState(null)

  const defaultCognito = {
    formId: "133",
    id: "991760a4-ddaf-572c-afd8-d35d6761b36e",
  }
  const finalCognitoForm = data?.cognitoForm?.formId
    ? data.cognitoForm
    : defaultCognito

  const [userInfo, setUserInfo] = useState(() => {
    if (typeof window === "undefined") return null
    try {
      const storedUserInfo = localStorage.getItem("user_info")
      return storedUserInfo ? JSON.parse(storedUserInfo) : null
    } catch (error) {
      return null
    }
  })

  const finalUtmData = useMemo(() => {
    if (typeof window === "undefined") return {}

    const utmKeys = [
      "utm_campaign",
      "utm_content",
      "utm_source",
      "utm_medium",
      "utm_term",
    ]

    // Fix the error here by removing the TypeScript type annotation
    const collectedUtms = {}
    const urlParams = new URLSearchParams(window.location.search)

    utmKeys.forEach(key => {
      const urlValue = urlParams.get(key)
      const storageValue = localStorage.getItem(key)

      if (urlValue) {
        collectedUtms[key] = urlValue
      } else if (storageValue) {
        collectedUtms[key] = storageValue
      }
    })

    return {
      UTMCampaign: collectedUtms.utm_campaign || "",
      UTMContent: collectedUtms.utm_content || "",
      UTMSource: collectedUtms.utm_source || "",
      UTMMedium: collectedUtms.utm_medium || "",
      UTMTerm: collectedUtms.utm_term || "",
    }
  }, [])

  const prefillData = useMemo(() => {
    if (!userInfo) {
      return {}
    }

    const embedUrl =
      typeof window !== "undefined"
        ? `https://${window.location.hostname}${window.location.pathname}`
        : ""

    return {
      FirstName: userInfo?.fName || "",
      LastName: userInfo?.lName || "",
      Email: userInfo?.email || "",
      TID: userInfo?.tid || "",
      SKU: product?.sku || "",
      EmbedURL: embedUrl,
      FormReferrerURL: embedUrl,
      ...finalUtmData,
    }
  }, [userInfo, product?.sku, finalUtmData])

  useEffect(() => {
    if (!product?.sku) {
      setError(true)
      setErrorText("The product you are looking for does not exist.")
    }
  }, [product])

  useEffect(() => {
    if (
      endedTrial &&
      data?.yourTrialHasEndedHeaderTextAndCta?.alignment === "left"
    ) {
      const selector = document.querySelector(
        "div.trial-form--grid-wrapper > div > div.t2-layout-faq.t2-layout-faq--t-t2-collapsed.t2-preset-theme-two.t2-color-setting--light > div > div > div.t2-layout-faq__cta.t2-layout__cta.t2-layout__bottom-cta"
      )
      if (selector) selector.style.justifyContent = "left"
    }
  }, [endedTrial])

  const userLoggedIn =
    typeof window !== "undefined"
      ? localStorage?.getItem("user_info")
        ? true
        : false
      : false

  useEffect(() => {
    if (userLoggedIn) {
      try {
        const storedUserInfo = localStorage.getItem("user_info")
        const parsedUserInfo = storedUserInfo
          ? JSON.parse(storedUserInfo)
          : null
        setUserInfo(parsedUserInfo)
      } catch (error) {
        setUserInfo(null)
      }
    } else {
      setUserInfo(null)
    }
  }, [userLoggedIn])

  const {
    queryString,
    redirect_uri,
    code_verifier,
    client_id,
    url,
    client_secret,
    signUpQuery,
  } = getConfigTID(siteIdentifier)

  const getHealthCheck = () => {
    // checkForHealth is first API call
    // This checks to see if middleware API works
    checkForHealth(
      setLoading,
      setError,
      setCheckHealthOfTrialsAPI,
      setErrorText,
      setAuth
    )
  }

  // Setting up checks for inactive auth token. We are using setInterval to periodically check if the token is expired
  const userTrialData = JSON.parse(localStorage.getItem("user-trial-data"))
  const timeStamp = userTrialData?.time

  const CHECK_INTERVAL = 1000 * 60 * 5 // Check every 5 minutes
  const INACTIVITY_TIMEOUT = 1000 * 60 * 50 // Refresh after 50 minutes of inactivity

  const activityTimer = useRef(null)
  const expirationCheckTimer = useRef(null)

  const resetActivityTimer = () => {
    if (activityTimer.current) {
      clearTimeout(activityTimer.current)
    }
    activityTimer.current = setTimeout(() => {
      refreshAuthToken(
        client_id,
        client_secret,
        redirect_uri,
        code_verifier,
        window?.location?.pathname,
        url,
        null,
        null,
        () => setAuth(true),
        setLoading
      )
    }, INACTIVITY_TIMEOUT)
  }

  const handleUserActivity = () => {
    resetActivityTimer()
  }

  useEffect(() => {
    // Check token expiration every 5 minutes while page is idling
    const checkTokenExpiration = () => {
      if (!getTrialFormId(Number(timeStamp))) {
        refreshAuthToken(
          client_id,
          client_secret,
          redirect_uri,
          code_verifier,
          window?.location?.pathname,
          url,
          null,
          null,
          () => setAuth(true),
          setLoading
        )
      }
    }

    // Run the check on mount and then at a set interval (5 minute interval)
    checkTokenExpiration()
    expirationCheckTimer.current = setInterval(
      checkTokenExpiration,
      CHECK_INTERVAL
    )

    // Set up event listeners to detect user activity
    window.addEventListener("mousemove", handleUserActivity)
    window.addEventListener("keypress", handleUserActivity)
    window.addEventListener("click", handleUserActivity)

    // Cleanup function to remove event listeners and clear timers
    return () => {
      clearInterval(expirationCheckTimer.current)
      clearTimeout(activityTimer.current)
      window.removeEventListener("mousemove", handleUserActivity)
      window.removeEventListener("keypress", handleUserActivity)
      window.removeEventListener("click", handleUserActivity)
    }
  }, [timeStamp, url])

  const checkIfUserHasTrial = async () => {
    // Start with loading true until we've checked everything
    setLoading(true)
    if (userLoggedIn) {
      try {
        const userTrialData = JSON.parse(
          localStorage.getItem("user-trial-data")
        )
        const timeStamp = userTrialData?.time
        const userId = userTrialData?.id

        // Check for a valid token and an active trial status
        if (timeStamp && getTrialFormId(Number(timeStamp)) && userId) {
          // user is authenticated
          setAuth(true)

          // Check for active trial status
          getTrialDetails(product?.sku, setActiveTrial, setLoading, setError)
        }
      } catch (e) {
        console.error("Error checking auth token:", e)
        setAuth(false)
        setLoading(false)
        setError(true)
        setError("Authentication error.")
      }
    } else {
      // User is not logged in
      setAuth(false)
      setLoading(false)
    }
  }

  useEffect(() => {
    if (typeof window !== "undefined") {
      checkIfUserHasTrial()
      if (userLoggedIn) {
        getHealthCheck()
      }
    }
  }, [userLoggedIn, product?.sku, checkHealthOfTrialsAPI])

  useEffect(() => {
    // Get trial information if a user has an existing trial. We don't want to render the form if the trial is already active
    const getLicensingData = () => {
      if (activeTrial && activeTrial?.length) {
        const getLicense = activeTrial?.find(trial => {
          return trial?.sku?.id === product?.sku
        })
        // Check to see if trial has expired
        if (getLicense?.status && getLicense?.status === "expired") {
          setDaysLeftOfTrial(0)
          setEndedTrial(true)
          // if trial is active, display days remaining
        } else if (getLicense?.status === "active") {
          const trialEndDate = dayjs(getLicense?.deactivationDate)
          setDaysLeftOfTrial(trialEndDate.diff(Date.now(), "day") + 1)
        }
      }
    }
    if (typeof window !== "undefined") {
      getLicensingData()
    }
  }, [activeTrial, product?.sku])

  useEffect(() => {
    if (userLoggedIn && typeof window !== "undefined") {
      if (activeTrial && activeTrial?.length && daysLeftOfTrial !== null) {
        const getLicense = activeTrial?.find(trial => {
          return trial?.sku?.id === product?.sku
        })
        triggerTrialStarted(
          data?.productName,
          daysLeftOfTrial,
          getLicense?.status
        )
      }
    }
  }, [
    userLoggedIn,
    activeTrial,
    daysLeftOfTrial,
    product?.sku,
    data?.productName,
  ]) // Added all necessary dependencies

  //formats data for components
  const formatProductSummaryCard = () => {
    if (!daysLeftOfTrial || daysLeftOfTrial === 0 || daysLeftOfTrial < 0) {
      return {
        name: data?.productName,
        description: data?.trialSummaryCardCustomText,
        productLine: `0 ${data?.trialLengthLabel || "Days "}`,
      }
    }
    return {
      name: data?.productName,
      description: data?.trialSummaryCardCustomText,
      productLine:
        daysLeftOfTrial && daysLeftOfTrial < 2
          ? `${daysLeftOfTrial} ${data?.trialLengthSingular || "Day"}`
          : `${daysLeftOfTrial} ${data?.trialLengthLabel || "Days "}`,
      ctas: [
        {
          text: data?.downloadTextForButtonAfterTrialHasStarted || "Download",
          url: product?.LaunchLink_Web,
          className: "primary--1",
        },
        {
          text: data?.viewYourProductsLabel,
          url: process.env.GATSBY_PRODUCTS_LINK
            ? process.env.GATSBY_PRODUCTS_LINK
            : "https://accounts.trimble.com/products",
          className: "primary--2",
        },
      ],
    }
  }

  //formats data for components
  const productSummary = {
    variant:
      !daysLeftOfTrial || daysLeftOfTrial === 0 || daysLeftOfTrial < 0
        ? "product-trial-last-day"
        : "product-trial",
    summaryCard: formatProductSummaryCard(),
    labels: {
      productSummaryLabel: data?.trialSummaryLabel,
      productLineLabel: data?.daysRemainingLabel,
    },
    columns: [
      {
        eyebrow: data?.inTrialProductSummary?.columnOneEyebrow,
        header: data?.inTrialProductSummary?.columnOneHeader,
        subheader: data?.inTrialProductSummary?.columnOneDescription,
        listType:
          data?.inTrialProductSummary?.columnOneBullets?.[0]?.__typename ===
          "ContentfulPoint"
            ? "bullets"
            : data?.inTrialProductSummary?.columnOneBullets?.[0]?.__typename ===
              "ContentfulLink"
            ? "links"
            : null,
        list: formatList(
          data?.inTrialProductSummary?.columnOneBullets,
          "en",
          null
        ),
      },
      {
        eyebrow: data?.inTrialProductSummary?.columnTwoEyebrow,
        header: data?.inTrialProductSummary?.columnTwoHeader,
        subheader: data?.inTrialProductSummary?.columnTwoDescription,
        listType:
          data?.inTrialProductSummary?.columnTwoBullets?.[0]?.__typename ===
          "ContentfulPoint"
            ? "bullets"
            : data?.inTrialProductSummary?.columnTwoBullets?.[0]?.__typename ===
              "ContentfulLink"
            ? "links"
            : null,
        list: formatList(
          data?.inTrialProductSummary?.columnTwoBullets,
          "en",
          null
        ),
      },
    ],
  }
  //formats data for components
  const trialSpecifics = {
    header:
      data?.trialFormStartLabel && data?.productName
        ? `${data?.trialFormStartLabel} ${data?.productName}`
        : `Start a Trial of ${data?.productName}`,
    text:
      product?.shortDescription ||
      "Connect the right people to the right data at the right time - free for 30 days!",
    signInText: data?.signInTextLabel,
    ctas: [
      {
        text: data?.signInLabel || "Sign In",
        onClick:
          typeof window !== "undefined"
            ? () =>
                window.open(
                  `${queryString}/${locale}/products/${data?.productName
                    .toLowerCase()
                    .replaceAll(" ", "-")}/trial`,
                  "_self"
                )
            : () => {},

        className: "primary--1",
      },
      {
        text: data?.createAnAccountLabel || "Create An Account",
        onClick: () =>
          window.open(
            `${signUpQuery}/${locale}/products/${data?.productName
              .toLowerCase()
              .replaceAll(" ", "-")}/trial`,
            "_self"
          ),
        className: "primary--2",
      },
    ],
  }

  //formats data for components
  const trialSummary = {
    eyebrow: data?.trialSummaryLabel || "Trial Summary",
    productName: data?.productName,
    productNameIcon: data?.productIcon?.file?.url
      ? {
          src: data?.productIcon?.file?.url,
          alt: data?.productIcon?.file?.fileName,
        }
      : null,
    body: data?.trialSummaryCustomText,
    bullets: data?.customContentListForTrialSummary?.length
      ? data?.customContentListForTrialSummary
      : [],
    durationLabel: data?.durationLabel || "Duration",
    duration:
      product?.Trial_Duration && product?.Trial_Duration < 10
        ? `${product?.Trial_Duration} ${data?.trialLengthSingular || "Day"}`
        : product?.Trial_Duration
        ? `${product?.Trial_Duration} ${data?.trialLengthLabel || "Days "}`
        : null,
    operatingSystemLabel: data?.operatingSystemTypeToDisplay
      ? data?.operatingSystemLabel || "Operating System"
      : "",
    operatingSystem: data?.operatingSystemTypeToDisplay,
  }

  //formats data for components
  const content = {
    trialSpecifics,
    trialSummary,
  }

  //formats data for components
  const buttons = data?.errorLinks?.length
    ? data?.errorLinks?.map(link => {
        return {
          primary: true,
          label: formatPageLinks(link, appData?.locale)?.text,
          destination: formatPageLinks(link, appData?.locale)?.url,
        }
      })
    : []

  const heroErrorContent = {
    orientation: "left",
    isHeaderBig: false,
    isWrapped: true,
    presetTheme: "t2-preset-theme-two",
    header: data?.anErrorOccurredLabel || "An Error has occured",
    description:
      data?.errorTextDescription ||
      "There was an issue creating your trial.  Please contact our sales team",
    buttons,

    image: data?.errorImage?.image?.file?.url
      ? {
          alt: data?.errorImage?.image?.title
            ? data?.errorImage?.image?.title
            : "error",
          x: 0,
          y: 0,
          title: data?.errorImage?.image?.title
            ? data?.errorImage?.image?.title
            : "error",
          height: 500,
          width: 500,
          url: data?.errorImage?.image?.file?.url,
        }
      : {
          src: errorImg,
          alt: "error page",
        },
  }

  const formatTextWithCTA = content => {
    if (content) {
      return {
        presetTheme: "t2-preset-theme-two",
        eyebrow: content?.eyebrow,
        header: content?.headline,
        description: content?.subhead,
        buttons: content?.ctaButtons?.map(btn => {
          return {
            primary: true,
            label: btn?.text,
            destination: formatPageLinks(btn, locale)?.url,
            Link,
          }
        }),
      }
    }
  }
  //formats data for components
  const renderQuestionsAndAnswers = () => {
    const accordionData =
      data?.ctaWithTextAndHeaderWithAccordionForTrials?.find(component => {
        return component?.__typename === "ContentfulAccordion"
      })

    const ctaWithHeader =
      data?.ctaWithTextAndHeaderWithAccordionForTrials?.find(component => {
        return component?.__typename === "ContentfulCtaWithTextAndHeader"
      })

    const formatAccordionAndCTASection = (cta, accordion) => {
      let ctaSection
      let accordionSection
      if (cta) {
        ctaSection = {
          eyebrow: cta?.eyebrow,
          header: cta?.headline,
          subheader: cta?.subhead,
          description: cta?.body?.childMarkdownRemark?.html,
          buttons: cta?.ctaButtons?.map(btn => {
            return {
              primary: true,
              label: btn?.text,
              destination: formatPageLinks(btn, locale)?.url,
              Link,
            }
          }),
        }
      }
      if (accordion) {
        accordionSection = accordion?.items?.map(item => {
          return {
            headline: item?.headline,
            description: item?.body?.childMarkdownRemark?.html,
          }
        })
      }

      return { ...ctaSection, faqItems: accordionSection }
    }
    return accordionData || ctaWithHeader ? (
      <div className="trial-form__question-container">
        <LayoutFAQs
          {...formatAccordionAndCTASection(ctaWithHeader, accordionData)}
        />
      </div>
    ) : (
      <></>
    )
  }

  const isDataReady = Boolean(
    !loading && auth && finalCognitoForm?.formId && product?.sku && userInfo
  )

  return (
    <section className="trial-form">
      <div className="trial-form--grid-wrapper">
        {loading && (
          <section className="trial-form__loading-spinner">
            <LoadingSpinner />
          </section>
        )}

        {!activeTrial && !loading && !error && !endedTrial && (
          <div className="trial-form__content-container">
            <section className="trial-form__content-container--forms">
              {/* Before Login */}
              {!auth && !loading && <TrialFormStart content={content} />}
              {/* After Logged before trial starts */}
              {isDataReady && (
                <EntitlementsForm
                  trialSummary={trialSummary}
                  cognitoForm={finalCognitoForm}
                  prefillData={prefillData}
                  appData={appData}
                  userInfo={userInfo}
                  productData={product}
                  startTrial={startTrial}
                  setLoading={setLoading}
                  setError={setError}
                  setErrorText={setErrorText}
                  loading={loading}
                />
              )}
            </section>
          </div>
        )}
        {auth &&
          activeTrial &&
          checkHealthOfTrialsAPI &&
          !endedTrial &&
          !loading &&
          !error && (
            <div className="trial-form__started-container">
              {/* Your trial has started */}
              {data?.inTrialMessagingHeaderTextAndCta && (
                <LayoutFAQs
                  {...formatTextWithCTA(data?.inTrialMessagingHeaderTextAndCta)}
                />
              )}
              {productSummary && <ProductSummary content={productSummary} />}
            </div>
          )}
        {auth && endedTrial && !loading && !error && (
          <div className="trial-form__ended-container">
            {/* Your trial has ended */}
            {data?.yourTrialHasEndedHeaderTextAndCta && (
              <LayoutFAQs
                {...formatTextWithCTA(data?.yourTrialHasEndedHeaderTextAndCta)}
              />
            )}
            {productSummary && <ProductSummary content={productSummary} />}
          </div>
        )}
        {/* Error state */}
        {error && !loading && (
          <div className="trial-form__error-container">
            {heroErrorContent && <LayoutGeneral {...heroErrorContent} />}
          </div>
        )}
      </div>

      {renderQuestionsAndAnswers()}
    </section>
  )
}

//formats data for components
const formatList = (list, locale, currentSpaceId) => {
  if (list && list.length) {
    if (list?.[0]?.__typename === "ContentfulPoint") {
      return list.map(item => {
        return { title: item?.header, text: item?.description }
      })
    }

    if (list?.[0]?.__typename === "ContentfulLink") {
      return list.map(item => {
        let itemUrl = item?.url?.url
        if (item?.url?.__typename === "ContentfulProductDetailPage") {
          itemUrl = createProductDetailPageLink(locale, item?.url)?.url
        }
        if (item?.url?.__typename === "ContentfulProductTrialPage") {
          itemUrl = createProductTrialPageLink(locale, item?.url)?.url
        }
        if (item?.url?.realLink?.url) {
          itemUrl = updateDownloadLink(item?.url?.realLink?.url, currentSpaceId)
        }
        if (item?.url?.internal?.type === "ContentfulPageBase") {
          itemUrl = `/${locale}/${item?.url?.url}`
        }
        if (item?.url?.internal?.type === "ContentfulProductPage") {
          itemUrl = `/${locale}/products/${item?.url?.url}`
        }
        return {
          url: itemUrl,
          text:
            item?.url?.__typename === "ContentfulProductDetailPage" ||
            item?.url?.__typename === "ContentfulProductTrialPage"
              ? item?.text
              : item?.url?.text,
          type:
            item?.url?.type && item?.url?.type === "ContentfulDownloadLink"
              ? "pdf"
              : null,
        }
      })
    }
  }
}

export default TrialWorkFlow
